import React, { useState, useEffect } from 'react';
import { useOutletContext, useSearchParams, Link } from 'react-router-dom';
import { 
  Search, ChevronLeft, ChevronRight, Image, User, 
  Calendar, Eye, Download, Filter, X, Loader2
} from 'lucide-react';
import { adminFetch } from './AdminLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

// Lazy-loading thumbnail component
const ArtworkThumbnail = ({ artworkId, intention, adminToken }) => {
  const [thumbnail, setThumbnail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const loadThumbnail = async () => {
      try {
        const data = await adminFetch(`/admin/artworks/${artworkId}/thumbnail`, {}, adminToken);
        if (data.thumbnail) {
          const type = data.type === 'svg' ? 'image/svg+xml' : 'image/png';
          setThumbnail(`data:${type};base64,${data.thumbnail}`);
        } else {
          setError(true);
        }
      } catch (err) {
        console.error('Failed to load thumbnail:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    loadThumbnail();
  }, [artworkId, adminToken]);

  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-900">
        <Loader2 className="w-8 h-8 text-gray-600 animate-spin" />
      </div>
    );
  }

  if (error || !thumbnail) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-900">
        <Image className="w-12 h-12 text-gray-600" />
      </div>
    );
  }

  return (
    <img 
      src={thumbnail}
      alt={intention}
      className="w-full h-full object-cover"
      loading="lazy"
    />
  );
};

const AdminArtworks = () => {
  const { adminToken } = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [artworks, setArtworks] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [selectedArtwork, setSelectedArtwork] = useState(null);
  const [filterType, setFilterType] = useState('all'); // all, real, test
  const limit = 12;
  
  const userIdFilter = searchParams.get('user_id') || '';

  // Test account patterns
  const isTestAccount = (email) => {
    if (!email) return false;
    const testPatterns = [
      /@test\.com$/i,
      /@mandala-test\.com$/i,
      /@mandalamuse-test\.com$/i,
      /^test[_-]/i,
      /^testing[_-]/i,
      /_test@/i,
      /test\d+@/i,
    ];
    return testPatterns.some(pattern => pattern.test(email));
  };

  const fetchArtworks = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ 
        skip: page * limit, 
        limit: limit,
        filter_type: filterType  // Send filter to backend
      });
      if (userIdFilter) params.append('user_id', userIdFilter);
      
      console.log('Fetching artworks with token:', adminToken ? 'present' : 'missing');
      const data = await adminFetch(`/admin/artworks?${params}`, {}, adminToken);
      console.log('Artworks API response:', data);
      
      if (!data || !data.artworks) {
        console.error('Invalid response structure:', data);
        setArtworks([]);
        setTotal(0);
        return;
      }
      
      setArtworks(data.artworks);
      setTotal(data.total);
    } catch (err) {
      console.error('Fetch artworks error:', err);
      toast.error(err.message || 'Failed to fetch artworks');
      setArtworks([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArtworks();
  }, [page, userIdFilter, adminToken, filterType]);

  const clearFilter = () => {
    setSearchParams({});
    setPage(0);
  };

  const handleFilterChange = (value) => {
    setFilterType(value);
    setPage(0);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      {/* Header with Filter */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Artworks</h1>
          <p className="text-gray-400 mt-1">{total} total artworks</p>
        </div>
        
        {/* Filter Dropdown */}
        <div className="flex items-center gap-2">
          <Filter size={18} className="text-gray-400" />
          <Select value={filterType} onValueChange={handleFilterChange}>
            <SelectTrigger className="w-[160px] bg-gray-800 border-gray-700 text-white">
              <SelectValue placeholder="Filter artworks" />
            </SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700">
              <SelectItem value="all" className="text-white hover:bg-gray-700">All Artworks</SelectItem>
              <SelectItem value="real" className="text-white hover:bg-gray-700">Real Users Only</SelectItem>
              <SelectItem value="test" className="text-white hover:bg-gray-700">Test Accounts Only</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Filter Badge */}
      {userIdFilter && (
        <div className="flex items-center gap-2">
          <span className="px-3 py-1 bg-blue-600/20 text-blue-400 rounded-full text-sm flex items-center gap-2">
            <Filter size={14} />
            Filtered by user
            <button onClick={clearFilter} className="hover:text-white">
              <X size={14} />
            </button>
          </span>
        </div>
      )}

      {/* Artworks Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : artworks.length === 0 ? (
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-12 text-center">
          <Image className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">No artworks found</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {artworks.map((artwork) => (
            <div 
              key={artwork.artwork_id}
              className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden hover:border-emerald-500/50 transition-colors group"
              data-testid={`artwork-card-${artwork.artwork_id}`}
            >
              {/* Image - Lazy loaded */}
              <div className="aspect-square relative bg-gray-900">
                <ArtworkThumbnail 
                  artworkId={artwork.artwork_id} 
                  intention={artwork.intention}
                  adminToken={adminToken}
                />
                
                {/* Overlay on hover */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button
                    onClick={() => setSelectedArtwork(artwork)}
                    className="p-2 bg-white/20 rounded-full hover:bg-white/30 text-white"
                  >
                    <Eye size={20} />
                  </button>
                </div>
              </div>

              {/* Info */}
              <div className="p-3">
                <p className="text-white font-medium text-sm truncate" title={artwork.intention}>
                  {artwork.intention}
                </p>
                <div className="flex items-center justify-between mt-2 text-xs text-gray-400">
                  <span className="flex items-center gap-1">
                    <User size={12} />
                    {artwork.user_info?.name || 'Unknown'}
                  </span>
                  <span>{artwork.geometry_framework}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-gray-800 border border-gray-700 rounded-xl p-4">
          <p className="text-sm text-gray-400">
            Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => p - 1)}
              disabled={page === 0}
              className="border-gray-600"
            >
              <ChevronLeft size={16} />
            </Button>
            <span className="text-gray-400 text-sm px-2">
              Page {page + 1} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => p + 1)}
              disabled={page >= totalPages - 1}
              className="border-gray-600"
            >
              <ChevronRight size={16} />
            </Button>
          </div>
        </div>
      )}

      {/* Artwork Detail Modal */}
      {selectedArtwork && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setSelectedArtwork(null)}>
          <div className="bg-gray-800 border border-gray-700 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">Artwork Details</h3>
              <button onClick={() => setSelectedArtwork(null)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            
            <div className="p-6 grid md:grid-cols-2 gap-6">
              {/* Image */}
              <div className="bg-gray-900 rounded-lg overflow-hidden">
                {selectedArtwork.main_image_base64 && (
                  <img 
                    src={`data:image/png;base64,${selectedArtwork.main_image_base64}`}
                    alt={selectedArtwork.intention}
                    className="w-full"
                  />
                )}
              </div>

              {/* Details */}
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-400">Intention</label>
                  <p className="text-white">{selectedArtwork.intention}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-400">Framework</label>
                    <p className="text-white">{selectedArtwork.geometry_framework}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-400">Tradition</label>
                    <p className="text-white">{selectedArtwork.tradition}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-400">Symmetry</label>
                    <p className="text-white">{selectedArtwork.symmetry_type}-fold</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-400">Output</label>
                    <p className="text-white">{selectedArtwork.output_profile || 'N/A'}</p>
                  </div>
                </div>
                <div>
                  <label className="text-sm text-gray-400">User</label>
                  <p className="text-white">
                    {selectedArtwork.user_info?.name || 'Unknown'} ({selectedArtwork.user_info?.email})
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-400">Created</label>
                  <p className="text-white">{new Date(selectedArtwork.created_at).toLocaleString()}</p>
                </div>
                
                {/* Meaning Card */}
                {selectedArtwork.meaning_card && (
                  <div className="border-t border-gray-700 pt-4">
                    <label className="text-sm text-gray-400">Meaning Card</label>
                    <p className="text-white mt-1">{selectedArtwork.meaning_card.interpretation}</p>
                  </div>
                )}

                <div className="flex gap-2 pt-4">
                  <Link
                    to={`/admin/users/${selectedArtwork.user_id}`}
                    className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-center transition-colors"
                  >
                    View User
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminArtworks;
